import React, { useState, useEffect } from 'react'
// import { useGetTeams } from '../services/api/team/useGetTeams'
import useGetTeams from '@/services/api/team/useGetTeams'
import useGetUsers from '@/services/api/account/useGetUsers'
import useGetRoles from '@/services/api/account/useGetRoles'
import useInviteUserNew from '@/services/api/account/useInviteUserNew'
// import useUpdateUserRole from '@/services/api/account/useUpdateUserRole'
import useGetCurrentUser from '@/services/api/auth/useGetCurrentUser'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import Field from '@/components/ui/Field'
import FieldStatusMessage from '@/components/ui/FieldStatusMessage'
import { Input } from '@/components/ui/Input'
import { UserPlus } from 'lucide-react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import useAuthStore from '@/store/useAuthStore'
import SpaceSelectComponent from './SpaceSelectComponent'
import UserCard from './UserCard'

const inviteSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  role_id: z.string().min(1, 'Please select a role'),
})

type InviteFormData = z.infer<typeof inviteSchema>

export default function UsersSection() {
  const currentTeamId = useAuthStore((state) => state.currentTeamId)

  const [selectedTeamId, setSelectedTeamId] = useState<string>(currentTeamId)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)

  const { data: teams, isLoading: teamsLoading } = useGetTeams()
  const {
    data: users,
    isLoading: usersLoading,
    status: usersStatus,
    refetch: refetchUsers,
  } = useGetUsers({ teamId: selectedTeamId })
  const { data: roles, isLoading: rolesLoading } = useGetRoles({
    teamId: selectedTeamId,
  })
  const { action: inviteUser, status: inviteStatus } = useInviteUserNew()

  //   const {
  //     action: updateUserRole,
  //     status: { isPending: updateRoleLoading },
  //   } = useUpdateUserRole()
  const { data: currentUser } = useGetCurrentUser()

  const inviteLoading = inviteStatus.status === 'pending'

  // const selectedTeam = teams.find((team) => team.id === selectedTeamId)
  const hasManageUsersPermission = true
  // selectedTeam?.role === 'Owner' || selectedTeam?.role === 'Admin' // Simplified check

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
  })

  const onInviteSubmit = async (data: InviteFormData) => {
    if (!selectedTeamId) return

    try {
      await inviteUser({
        teamId: selectedTeamId,
        email: data.email,
        role: data.role_id,
      })
      toast.success('User invited successfully')
      setInviteDialogOpen(false)
      reset()
      refetchUsers()
    } catch (error) {
      toast.error('Failed to invite user')
    }
  }

  if (!hasManageUsersPermission && selectedTeamId) {
    return (
      <div className="space-y-6">
        <div>
          <h2>Users</h2>
          <p className="text-muted-foreground">
            Manage team members and their roles
          </p>
        </div>
        <Separator />
        <Card className="w-full p-6 text-center">
          <p className="text-muted-foreground">
            You don't have permission to manage users in this space.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2>Users</h2>
        <p className="text-muted-foreground">
          Manage team members and their roles
        </p>
      </div>

      <Separator />

      <div className="space-y-4">
        <SpaceSelectComponent
          selectedTeamId={selectedTeamId}
          onValueChange={(val) => {
            setSelectedTeamId(val)
          }}
        />
        <div></div>

        {selectedTeamId && hasManageUsersPermission && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3>Team Members</h3>
              <Dialog
                open={inviteDialogOpen}
                onOpenChange={setInviteDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Invite New User</DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <form
                      onSubmit={handleSubmit(onInviteSubmit)}
                      className="space-y-4"
                    >
                      <Field
                        statusText={errors.email?.message}
                        status={errors.email ? 'error' : null}
                      >
                        <label>Email Address</label>
                        <Input
                          {...register('email')}
                          type="email"
                          placeholder="user@example.com"
                        />
                        <FieldStatusMessage />
                      </Field>

                      <Field
                        statusText={errors.role_id?.message}
                        status={errors.role_id ? 'error' : null}
                      >
                        <label>Role</label>
                        <Select
                          onValueChange={(value) => setValue('role_id', value)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                          <SelectContent>
                            {rolesLoading ? (
                              <SelectItem value="loading-roles" disabled>
                                Loading roles...
                              </SelectItem>
                            ) : (
                              roles.map((role) => (
                                <SelectItem key={role} value={role}>
                                  {role}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <FieldStatusMessage />
                      </Field>

                      <div className="flex justify-end space-x-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setInviteDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" disabled={inviteLoading}>
                          {inviteLoading ? 'Inviting...' : 'Send Invitation'}
                        </Button>
                      </div>
                    </form>
                  </DialogBody>
                </DialogContent>
              </Dialog>
            </div>

            {usersStatus === 'pending' ? (
              <Card className="w-full p-6">
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                  <span>Loading users...</span>
                </div>
              </Card>
            ) : (
              <div className="space-y-3">
                {users?.map((user) => (
                  <UserCard
                    key={user.id}
                    user={user}
                    currentUserId={currentUser?.id}
                    selectedTeamId={selectedTeamId}
                    onUserUpdated={refetchUsers}
                  />
                ))}

                {users?.length === 0 && (
                  <Card className="p-6 text-center">
                    <p className="text-muted-foreground">
                      No users found in this space.
                    </p>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
